import React from "react";

import { File, Pencil, Trash2 } from "lucide-react";

import { restrictionsColumn } from "../../common/restrictionsColumn";
import DataTableColumnHeader from "../../dataTable/DataTableColumnHeader";
import {
  actionsColumn,
  activityStatusOptions,
  countColumn,
  dateColumn,
  statusColumn,
} from "../../dataTable/dataTableColumns";
import DataTableLinkCell from "../../dataTable/DataTableLinkCell";
import { displayPath, PROJECTS_ROUTE } from "../../routes/routes";

import type { TFunction } from "i18next";

import type { DisplayRow } from "./displayColumns.types";
import type { DataTableColumnDef } from "../../dataTable/dataTable.types";

interface DisplayColumnsOptions {
  t: TFunction;
  projectSlug: string;
  onEdit: (display: DisplayRow) => void;
  onDelete: (display: DisplayRow) => void;
}

export function getDisplayHref(projectSlug: string, uuid: string): string {
  return (
    displayPath(projectSlug, uuid) ??
    `${PROJECTS_ROUTE}/${projectSlug}/displays`
  );
}

export function displayColumns({
  t,
  projectSlug,
  onEdit,
  onDelete,
}: DisplayColumnsOptions): DataTableColumnDef<DisplayRow>[] {
  return [
    statusColumn<DisplayRow>("status", t("status"), activityStatusOptions(t)),
    {
      accessorKey: "label",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t("title")} />
      ),
      meta: { searchable: true },
      cell: ({ row }) => (
        <DataTableLinkCell
          href={getDisplayHref(projectSlug, row.original.uuid)}
        >
          {row.original.label}
        </DataTableLinkCell>
      ),
    },
    restrictionsColumn<DisplayRow>({ t }),
    // L'identifiant `elements` est la clé d'URL.
    {
      ...countColumn<DisplayRow>("medias_count", t("elements"), <File />, {
        width: "10%",
      }),
      id: "elements",
    },
    dateColumn<DisplayRow>("updated_at", t("update_date")),
    actionsColumn<DisplayRow>(
      (display) => [
        {
          label: t("edit"),
          icon: <Pencil />,
          onClick: () => onEdit(display),
        },
        {
          label: t("delete"),
          icon: <Trash2 />,
          variant: "destructive",
          onClick: () => onDelete(display),
          confirm: {
            title: t("deleteTitle"),
            description: t("deleteBody", { displayTitle: display.label }),
            confirmLabel: t("deleteConfirm"),
          },
        },
      ],
      (display) => display.label,
    ),
  ];
}
